import { apiClient } from '../lib/api-client';

/**
 * Membership role enum — mirrors backend MembershipRole.
 */
export type MembershipRole = 'owner' | 'admin' | 'member';

/**
 * Organization DTO returned by the BFF.
 */
export interface OrganizationDto {
  id: string;
  name: string;
  slug?: string;
  plan?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Organization membership DTO — includes org details and membership metadata.
 */
export interface OrgMembershipDto {
  orgId: string;
  organization: OrganizationDto;
  role: MembershipRole;
  isDefault: boolean;
}

/**
 * Member DTO — a user within an organization.
 */
export interface OrgMemberDto {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: MembershipRole;
  joinedAt?: string;
}

/**
 * Switch org response — contains the new JWT token.
 */
export interface SwitchOrgResponse {
  data: {
    token: string;
    user: { id: string; email: string };
    expires: string;
  };
}

/**
 * Set default org response.
 */
export interface SetDefaultOrgResponse {
  success: boolean;
  defaultOrgId: string;
}

/**
 * API functions for organization management.
 * Uses the centralized apiClient for automatic token attachment and 401 handling.
 */
export const organizationsApi = {
  // ─── Organization CRUD ──────────────────────────────────────

  /** Create a new organization. Caller becomes owner. */
  create: (data: { name: string }) => apiClient.post<OrganizationDto>('/organizations', data),

  /** List all organizations the authenticated user belongs to. */
  findAll: () => apiClient.get<OrganizationDto[]>('/organizations'),

  /** Get organization by ID. */
  findById: (orgId: string) => apiClient.get<OrganizationDto>(`/organizations/${orgId}`),

  /** Update an organization (admin+ only). */
  update: (orgId: string, data: { name?: string }) =>
    apiClient.patch<OrganizationDto>(`/organizations/${orgId}`, data),

  /** Delete an organization (owner only). */
  remove: (orgId: string) => apiClient.delete<{ success: boolean }>(`/organizations/${orgId}`),

  // ─── Org Context ────────────────────────────────────────────

  /** Get user's org memberships with default indicator and role. */
  getMyMemberships: () => apiClient.get<OrgMembershipDto[]>('/organizations/my-memberships'),

  /** Switch active organization — validates membership, re-issues JWT. */
  switchOrg: (orgId: string) => apiClient.post<SwitchOrgResponse>(`/organizations/${orgId}/switch`),

  /** Set default organization for the authenticated user. */
  setDefaultOrg: (orgId: string) =>
    apiClient.post<SetDefaultOrgResponse>(`/organizations/${orgId}/set-default`),

  // ─── Membership Management ──────────────────────────────────

  /** List all members of an organization. */
  getMembers: (orgId: string) => apiClient.get<OrgMemberDto[]>(`/organizations/${orgId}/members`),

  /** Add a member to an organization (admin+ only). */
  addMember: (orgId: string, data: { userId: string; role?: MembershipRole }) =>
    apiClient.post<OrgMemberDto>(`/organizations/${orgId}/members`, data),

  /** Update a member's role (admin+ only). */
  updateMemberRole: (orgId: string, userId: string, data: { role: MembershipRole }) =>
    apiClient.patch<OrgMemberDto>(`/organizations/${orgId}/members/${userId}`, data),

  /** Remove a member from an organization (admin+ only). */
  removeMember: (orgId: string, userId: string) =>
    apiClient.delete<{ success: boolean }>(`/organizations/${orgId}/members/${userId}`),
};
